import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    EDIT, VIEW,
    IDLE, PENDING,
    ASC,
    COMPLIANCE,
} from "const"
import { CcLibrarySet } from "server/models"
import { hasProjectAccess } from "client/utils"
import {
    PRM_CC_COMPLIANCE,
    PRM_CC_RESULTS_PDF,
    StoreType,
    CcComplianceProps,
    CcLibrarySetList,

    LoadCcApprovalLevels,
    LoadCcResponses,
    LoadAllProjectGroupUsers,
    LoadAllCcLibrarySets,
    LoadNestedItems,
    LoadCcComplianceRevisions,
    LoadCcComplianceRevisionData,
    CreateCcComplianceRevision,
    SortCcLibrarySets,

    setProjectGroupUserList,
    setCcApprovalLevelList,
    setCcModuleApprovalLevelList,
    setCcResponseList,
    setCcModuleResponseList,
    setCcComplianceSetList,
    setCcComplianceSetIds,
    setCcComplianceItemList,
    setCcComplianceItemGroupedIds,
    setCcComplianceRevisionList,
    setCcComplianceRevisionActive,
    setCcComplianceVisibleItemIds,
    setCcComplianceListLoadState,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
    Button,

    LangReportWrapper,
    LangReportSwitcher,

    Table,
    TableGroupedIds,
    OnGetDisplayedIds,

    CheckRecentVersions,
} from "client/components"

import ExportToPDF from "client/pages/cc/exportToPDF"

import Columns from "./table/columns"
import TableBottom from "./table/bottom"
import SetCustomView from "./table/setCustomView"
import PopupMenu from "./popupMenu"
import DisplayComplianceColumns from "./displayColumns"
import Open from "./open"

const CcComplianceList = ({
    setId = null,
    revisionId = null,
    itemId = null,
}: CcComplianceProps) => {
    const tableClass = useSelector((state: StoreType) => state.ccCompliance.tableClass)
    const baseRoute = useSelector((state: StoreType) => state.ccCompliance.baseRoute)
    const itemList = useSelector((state: StoreType) => state.ccCompliance.itemList)
    const itemGroupedIds = useSelector((state: StoreType) => state.ccCompliance.itemGroupedIds)
    const setIds = useSelector((state: StoreType) => state.ccCompliance.setIds)
    const loadState = useSelector((state: StoreType) => state.ccCompliance.listLoadState)
    const reloadList = useSelector((state: StoreType) => state.ccCompliance.reloadList)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [groupedIds, setGroupedIds] = useState<TableGroupedIds[]>([])
    const [collapsedGroupes, setCollapsedGroupes] = useState<boolean>(true) // collapse top level by default
    const [showExport, setShowExport] = useState(false)

    useEffect(() => {
        if (!reloadList) return
        dispatch(setCcComplianceListLoadState(PENDING))
        Promise.all([
            // check recent versions for existing files
            CheckRecentVersions(project.id),

            // compliance approval levels
            LoadCcApprovalLevels(userData.accountId, project.id, 1)
            .then((res) => {
                dispatch(setCcApprovalLevelList(res.levelList))
                dispatch(setCcModuleApprovalLevelList({ 1: res.levelIds }))
            }),
            // compliance responses
            LoadCcResponses(userData.accountId, project.id, 1)
            .then((res) => {
                dispatch(setCcResponseList(res.responseList))
                dispatch(setCcModuleResponseList({ 1: res.responseIds }))
            }),
            // responsible responses
            LoadCcResponses(userData.accountId, project.id, 0)
            .then((res) => {
                dispatch(setCcResponseList(res.responseList))
                dispatch(setCcModuleResponseList({ 0: res.responseIds }))
            }),
            LoadAllProjectGroupUsers(userData.accountId, project.id)
            .then((res) => {
                dispatch(setProjectGroupUserList(res))
            }),
            LoadAllCcLibrarySets(userData.accountId, project.id)
            .then((sets) => {
                const setList: CcLibrarySetList = {}
                Object.values(sets).forEach((set: CcLibrarySet) => {
                    set.modules.forEach((row) => {
                        if (row.module === COMPLIANCE) {
                            setList[set.id] = set
                        }
                    })
                })

                const sortedIds = SortCcLibrarySets(Object.values(setList), "name", ASC, {})
                dispatch(setCcComplianceSetIds(sortedIds))
                dispatch(setCcComplianceSetList(setList))

                const promiseArray: Promise<any>[] = []
                Object.values(setList).forEach((set) => {
                    promiseArray.push(
                        LoadNestedItems(set.id).then((res) => {
                            dispatch(setCcComplianceItemList(res.itemList))
                            dispatch(setCcComplianceItemGroupedIds(set.id, res.nested))
                        }),
                        LoadCcComplianceRevisions(set.id).then((revisions) => {
                            // if revisions exist
                            if (revisions.length) {
                                const lastRevision = revisions[revisions.length - 1]
                                dispatch(setCcComplianceRevisionList(set.id, revisions))
                                dispatch(setCcComplianceRevisionActive(set.id, lastRevision))

                                return LoadCcComplianceRevisionData(lastRevision, dispatch)
                            }

                            // if no revisions - create new
                            return CreateCcComplianceRevision(set.id).then((newRevision) => {
                                dispatch(setCcComplianceRevisionList(set.id, [newRevision]))
                                dispatch(setCcComplianceRevisionActive(set.id, newRevision))

                                return LoadCcComplianceRevisionData(newRevision, dispatch)
                            })
                        }),
                    )
                })
                return Promise.all(promiseArray)
            }),
        ]).then(() => {
            dispatch(setCcComplianceListLoadState(IDLE))
            setCollapsedGroupes(false)
        })
    }, [reloadList])

    useEffect(() => {
        // open answer by url
        if (setId && revisionId && itemId && baseRoute) Open(setId, revisionId, itemId, baseRoute, dispatch)
    }, [setId, revisionId, itemId, baseRoute])

    useEffect(() => {
        // order groupedIds by setIds
        const grouped: TableGroupedIds[] = []
        setIds.forEach((id) => {
            if (itemGroupedIds[id]) grouped.push(itemGroupedIds[id])
        })
        setGroupedIds(grouped)
    }, [setIds, itemGroupedIds])

    const getDisplayedIds: OnGetDisplayedIds = (itemIds: number[]) => {
        dispatch(setCcComplianceVisibleItemIds(itemIds))
    }

    const editAccess = hasProjectAccess(PRM_CC_COMPLIANCE, EDIT)
    const viewPdfAccess = hasProjectAccess(PRM_CC_RESULTS_PDF, VIEW)

    const { columns, displayColumns } = Columns(editAccess)

    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.compliance}</PageTitle>
                {
                    viewPdfAccess && (
                        <LangReportWrapper>
                            <LangReportSwitcher />
                            <Button
                                variant="success"
                                disabled={loadState === PENDING}
                                onClick={() => setShowExport(true)}
                            >
                                {lang.export}
                            </Button>
                        </LangReportWrapper>
                    )
                }
            </PageHeader>
            <PageContent>
                {
                    loadState === PENDING
                    && <Loader />
                }

                <Table
                    className={tableClass}
                    columns={columns}
                    dataList={itemList}
                    dataGroupedIds={groupedIds}
                    collapsedGroupes={collapsedGroupes}
                    onGetDisplayedIds={getDisplayedIds}
                    rowSize="fit"
                    showPagination={false}
                    showTotal={false}
                    bottomContent={editAccess && <TableBottom />}
                    CustomViewLevel1={SetCustomView}
                />

                <PopupMenu />
                <DisplayComplianceColumns
                    columns={columns.filter((column) => !!column.header)} // don't show management columns
                    displayColumns={displayColumns}
                />

                <ExportToPDF
                    show={showExport}
                    defaultReport={COMPLIANCE}
                    onClose={() => setShowExport(false)}
                />
            </PageContent>
        </Container>
    )
}
export default CcComplianceList
